import type { ThreadData } from '@liveblocks/client'
import { useCreateComment, useSelf } from '@liveblocks/react'
import { ChevronsDownUpIcon, ChevronsUpDownIcon } from 'lucide-react'
import { useState } from 'react'

import { type EventProperties, useTrackCallback } from '@/lib/analytics/events'

import { AuthorLine } from '@/components/lemmala-design-system/author-line/author-line'
import { Button } from '@/components/lemmala-design-system/button/button'
import { CommentThread } from '@/components/shared/comment/comment-thread'
import { useSendCommentNotification } from '@/components/shared/comment/use-send-comment-notification'
import { hasMention, visibleComments } from '@/components/shared/comment/utils'

import { AssessmentCommentComposer } from './assessment-comment-composer'
import { ASSESSMENT_COMMENTS_LABELS, repliesLabel } from './labels'

type AssessmentCommentThreadProps = {
  assessmentId: string
  thread: ThreadData
  readOnly?: boolean
  highlightedCommentId?: string | null
  trackingProps?: EventProperties
}

export const AssessmentCommentThread = ({
  assessmentId,
  thread,
  readOnly,
  highlightedCommentId,
  trackingProps,
}: AssessmentCommentThreadProps) => {
  const createComment = useCreateComment()
  const sendNotification = useSendCommentNotification()
  const trackReplied = useTrackCallback('assessment.comment.replied')
  const trackThreadExpanded = useTrackCallback('assessment.comment.thread.expanded')
  const self = useSelf()
  const [isExpanded, setIsExpanded] = useState(true)
  const [isReplying, setIsReplying] = useState(false)

  const comments = visibleComments(thread)
  if (comments.length === 0) return null

  const replyCount = comments.length - 1
  const canReply = !readOnly && isExpanded

  return (
    <div className='flex flex-col gap-3 rounded border border-main-border-default bg-main-background-card p-3'>
      <CommentThread
        comments={isExpanded ? comments : comments.slice(0, 1)}
        readOnly={readOnly}
        highlightedCommentId={highlightedCommentId}
        trackingProps={trackingProps}
        hadReplies={replyCount > 0}
        className='gap-3'
      />

      {replyCount > 0 && (
        <Button
          variant='link'
          size='sm'
          className='w-fit gap-1 px-0'
          onClick={() => {
            if (!isExpanded) {
              trackThreadExpanded({
                ...trackingProps,
                parent_comment_id: comments[0]?.id,
                reply_count: replyCount,
              })
            }
            setIsExpanded((expanded) => !expanded)
          }}
        >
          {isExpanded ? (
            <ChevronsDownUpIcon className='size-4' />
          ) : (
            <ChevronsUpDownIcon className='size-4' />
          )}
          {isExpanded ? ASSESSMENT_COMMENTS_LABELS.collapse : repliesLabel(replyCount)}
        </Button>
      )}

      {canReply &&
        (isReplying ? (
          <AssessmentCommentComposer
            autoFocus
            placeholder={ASSESSMENT_COMMENTS_LABELS.replyPlaceholder}
            onSubmit={(comment) => {
              const reply = createComment({ threadId: thread.id, body: comment.body })
              setIsReplying(false)
              trackReplied({
                ...trackingProps,
                has_mention: hasMention(comment.body),
                parent_comment_id: comments[0]?.id,
              })
              sendNotification({
                assessmentId,
                body: comment.body,
                threadId: thread.id,
                commentId: reply.id,
                participantEmails: comments.map((existing) => existing.userId),
                trackingProps,
              })
            }}
          />
        ) : (
          <AuthorLine name={self?.info?.name ?? ''} avatarUrl={self?.info?.avatar} hideName>
            <button
              type='button'
              onClick={() => setIsReplying(true)}
              className='flex-1 rounded border border-main-border-semiBold px-2 py-1.5 text-left text-main-foreground-placeholder text-body-default hover:border-main-border-bold'
            >
              {ASSESSMENT_COMMENTS_LABELS.replyPlaceholder}
            </button>
          </AuthorLine>
        ))}
    </div>
  )
}
